import { ASSET_URLS } from '../constants'
import { borders, colors, lightTokens, spacing, typography } from '../tokens/internal'

interface StyleRule {
  selector: string
  declarations: Record<string, string>
}

/** Email clients (notably Outlook) ignore `rem`, so every length is emitted in px. */
const px = (value: string): string =>
  value.endsWith('rem') ? `${parseFloat(value) * 16}px` : value

const EMAIL_FONT_FAMILY = 'avenir-roman, sans-serif'

const EMAIL_HEADER_STYLE = { 'font-family': EMAIL_FONT_FAMILY }

const EMAIL_HEADER_LOGO_STYLE = {
  width: '85px',
  height: 'auto',
  'margin-bottom': px(spacing.compact),
}

const EMAIL_HEADER_NAME_STYLE = {
  'font-size': px(typography.fontSize.xxl),
  'line-height': typography.lineHeight.tight,
  'font-family': EMAIL_FONT_FAMILY,
  color: lightTokens.text.primary,
  'margin-top': '0',
  'margin-bottom': px(spacing.compact),
  'font-weight': 'normal',
}

const EMAIL_DIVIDER_STYLE = {
  border: 'none',
  'border-top': `${px(borders.width.thin)} solid ${colors.neutral[400]}`,
  margin: `0 0 ${px(spacing.sm)} 0`,
}

const EMAIL_FOOTER_DIVIDER_STYLE = {
  ...EMAIL_DIVIDER_STYLE,
  margin: `${px(spacing.xl)} 0 ${px(spacing.sm)} 0`,
}

const EMAIL_FOOTER_ICON_STYLE = {
  width: '20px',
  height: '20px',
  'object-fit': 'contain',
  'vertical-align': 'middle',
  'margin-right': px(spacing.micro),
}

const EMAIL_FOOTER_TEXT_STYLE = {
  'font-size': px(typography.fontSize.xxs),
  color: lightTokens.text.secondary,
  'font-family': EMAIL_FONT_FAMILY,
  'line-height': px(spacing.sm),
  'vertical-align': 'middle',
}

/**
 * Styling the editor applies through class names and stylesheets, expressed as
 * inline declarations so the saved HTML renders identically outside the app.
 */
const STYLE_RULES: StyleRule[] = [
  {
    selector: 'p',
    declarations: {
      'margin-top': '0',
      'margin-bottom': px(spacing.xxs),
      'line-height': px(spacing.sm),
    },
  },
  {
    selector: 'ul, ol',
    declarations: {
      'padding-left': px(spacing.sm),
      'list-style-position': 'outside',
      'margin-bottom': px(spacing.xxs),
    },
  },
  { selector: 'li', declarations: { 'margin-bottom': px(spacing.xxs) } },
  {
    selector: 'a',
    declarations: {
      color: lightTokens.secondary.main,
      'font-weight': String(typography.fontWeight.bold),
      'text-decoration': 'underline',
    },
  },
  { selector: 'img', declarations: { 'max-width': '100%', height: 'auto' } },
  { selector: 'table', declarations: { 'border-collapse': 'collapse', width: '100%' } },
  {
    selector: 'td, th',
    declarations: {
      border: `${px(borders.width.thin)} solid ${colors.neutral[300]}`,
      padding: px(spacing.xxs),
      'min-width': '50px',
    },
  },
  {
    selector: 'th',
    declarations: {
      'background-color': colors.neutral[100],
      'font-weight': String(typography.fontWeight.bold),
    },
  },
  {
    selector: 'blockquote',
    declarations: {
      'border-left': `${px(borders.width.thick)} solid ${colors.neutral[300]}`,
      'padding-left': px(spacing.compact),
      'margin-left': '0',
      color: lightTokens.text.secondary,
    },
  },
  { selector: '.email-header', declarations: EMAIL_HEADER_STYLE },
  { selector: '.email-header-logo', declarations: EMAIL_HEADER_LOGO_STYLE },
  { selector: '.email-header-name', declarations: EMAIL_HEADER_NAME_STYLE },
  { selector: '.email-divider', declarations: EMAIL_DIVIDER_STYLE },
  { selector: '.email-footer .email-divider', declarations: EMAIL_FOOTER_DIVIDER_STYLE },
  { selector: '.email-footer-icon', declarations: EMAIL_FOOTER_ICON_STYLE },
  { selector: '.email-footer-text', declarations: EMAIL_FOOTER_TEXT_STYLE },
]

const escapeForHtml = (str: string): string =>
  str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const parseStyleAttribute = (style: string): Record<string, string> =>
  style.split(';').reduce<Record<string, string>>((declarations, declaration) => {
    const separator = declaration.indexOf(':')
    if (separator === -1) return declarations
    const property = declaration.slice(0, separator).trim()
    const value = declaration.slice(separator + 1).trim()
    if (property && value) declarations[property] = value
    return declarations
  }, {})

const serializeDeclarations = (declarations: Record<string, string>): string =>
  Object.entries(declarations)
    .map(([property, value]) => `${property}: ${value}`)
    .join('; ')

const buildEmailHeaderInnerHtml = (logoUrl: string, organizationName: string): string =>
  `<img src="${escapeForHtml(logoUrl || ASSET_URLS.logo)}" alt="${escapeForHtml(organizationName)}" class="email-header-logo">` +
  `<h1 class="email-header-name">${escapeForHtml(organizationName)}</h1>` +
  `<hr class="email-divider">`

const buildEmailFooterInnerHtml = (): string =>
  `<hr class="email-divider">` +
  `<div class="email-footer-content">` +
  `<img src="${ASSET_URLS.icon}" alt="Polimorphic" class="email-footer-icon">` +
  `<span class="email-footer-text">Powered by Polimorphic</span>` +
  `</div>`

/**
 * Turn editor HTML into standalone HTML suitable for storing on
 * `MessageTemplate.body` and emailing:
 *  - expands the `email-header`/`email-footer` placeholder nodes into real markup
 *  - empties the `ai-reply-body` slot, which only the backend fills in
 *  - inlines the editor's stylesheet onto each element, without overriding
 *    styling the author set through the editor
 *  - keeps otherwise-collapsed empty paragraphs as blank lines
 *
 * The output still parses back into the editor's custom nodes, so it round-trips
 * through the form letter editor.
 */
export const inlineFormLetterStyles = (html: string): string => {
  const container = document.createElement('div')
  container.innerHTML = html

  container.querySelectorAll('[data-type="email-header"]').forEach((header) => {
    header.classList.add('email-header')
    header.innerHTML = buildEmailHeaderInnerHtml(
      header.getAttribute('data-logo-url') || '',
      header.getAttribute('data-organization-name') || '',
    )
  })

  container.querySelectorAll('[data-type="email-footer"]').forEach((footer) => {
    footer.classList.add('email-footer')
    footer.innerHTML = buildEmailFooterInnerHtml()
  })

  // The AI reply slot is a marker the backend swaps for the generated reply, so it
  // carries no content of its own outside the editor.
  container.querySelectorAll('[data-type="ai-reply-body"]').forEach((slot) => {
    slot.classList.add('email-ai-reply-body')
    slot.innerHTML = ''
  })

  const declarationsByElement = new Map<Element, Record<string, string>>()
  STYLE_RULES.forEach(({ selector, declarations }) => {
    container.querySelectorAll(selector).forEach((element) => {
      declarationsByElement.set(element, {
        ...declarationsByElement.get(element),
        ...declarations,
      })
    })
  })

  declarationsByElement.forEach((declarations, element) => {
    const authored = parseStyleAttribute(element.getAttribute('style') || '')
    element.setAttribute('style', serializeDeclarations({ ...declarations, ...authored }))
  })

  // Empty paragraphs are the editor's blank lines, but an empty <p> collapses outside the editor's stylesheet.
  container.querySelectorAll('p').forEach((paragraph) => {
    if (!paragraph.textContent?.trim() && paragraph.children.length === 0) {
      paragraph.innerHTML = '&nbsp;'
    }
  })

  return container.innerHTML
}

const AI_REPLY_PLACEHOLDER_HTML =
  '<span class="email-ai-reply-body-label">' +
  'AI reply goes here (only used when this letter is attached to an AI email)</span>'

export const labelAIReplySlotsInHtml = (html: string): string => {
  const container = document.createElement('div')
  container.innerHTML = html
  container.querySelectorAll('[data-type="ai-reply-body"]').forEach((slot) => {
    slot.classList.add('email-ai-reply-body')
    slot.innerHTML = AI_REPLY_PLACEHOLDER_HTML
  })
  return container.innerHTML
}

// Show where AI agent replies lands in a stored form letter, even if the stored form letter predates the AI reply slot
export const previewAIReplyPlacement = (html: string): string => {
  if (html.includes('data-type="ai-reply-body"')) return labelAIReplySlotsInHtml(html)

  const container = document.createElement('div')
  container.innerHTML = html

  const placeholder = document.createElement('div')
  placeholder.className = 'email-ai-reply-body'
  placeholder.innerHTML = AI_REPLY_PLACEHOLDER_HTML
  const footer = container.querySelector('[data-type="email-footer"]')
  container.insertBefore(placeholder, footer)

  return container.innerHTML
}
